import { useCallback, useState } from 'react';
import * as voicifyApi from '../api';
import ApplicationModel from '../models/applications/api/ApplicationModel';
import AppliedApplicationTemplateContainer from '../state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../state/containers/ApplicationEnvironmentsContainer';
import {
    ManagedWorkQueueAcceptedJob
} from '../models/managedWorkQueues/ManagedWorkQueue';

export interface BulkOperationResult {
    successCount: number;
    errorCount: number;
    errors: string[];
    batchId?: string;
    acceptedJobs: ManagedWorkQueueAcceptedJob[];
    validationFailures: Array<{ applicationId: string; error: string }>;
}

export interface UseBulkAppOperationsOptions {
    organizationId?: string;
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
    applicationEnvironmentsContainer?: ApplicationEnvironmentsContainer;
}

export interface UseBulkAppOperationsReturn {
    isProcessing: boolean;
    logs: string[];
    syncSingleModule: (appId: string, moduleId: string, syncName: string, denom: number | string, numerator: number | string) => Promise<string | null>;
    syncApps: (apps: ApplicationModel[], sharedTopicsApp?: ApplicationModel | null, concurrencyLimit?: number, preserveLogs?: boolean) => Promise<BulkOperationResult>;
    syncAndPublishApps: (apps: ApplicationModel[], sharedTopicsApp?: ApplicationModel | null, concurrencyLimit?: number, preserveLogs?: boolean) => Promise<BulkOperationResult>;
    publishApps: (apps: ApplicationModel[], concurrencyLimit?: number, preserveLogs?: boolean) => Promise<BulkOperationResult>;
    appendLog: (message: string) => void;
    clearLogs: () => void;
}

// Bulk operations are submitted as one server-side batch. Redis/SQS coordination and
// progress monitoring continue independently of this hook and the browser lifecycle.
export const useBulkAppOperations = (options: UseBulkAppOperationsOptions): UseBulkAppOperationsReturn => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [logs, setLogs] = useState<string[]>([]);

    const addLog = useCallback((message: string) => setLogs(previous => [...previous, message]), []);
    const clearLogs = useCallback(() => setLogs([]), []);

    const syncSingleModule = useCallback(async (
        appId: string,
        moduleId: string,
        syncName: string,
        _denom: number | string,
        _numerator: number | string
    ): Promise<string | null> => {
        const moduleResult = await voicifyApi.findAppliedApplicationTemplate(moduleId);
        if (moduleResult.resultType !== 'Ok') return moduleResult.errors?.join(', ') || `Unable to load ${syncName}.`;
        const queueResult = await voicifyApi.queueUpdateApplicationFromTemplateFormsSync(appId, moduleId, {
            templateFormFieldValues: moduleResult.data.templateFormFieldValues,
            dynamicFormSections: moduleResult.data.dynamicFormSections || []
        });
        return queueResult.resultType === 'Ok' ? null : queueResult.errors?.join(', ') || `Unable to queue ${syncName}.`;
    }, []);

    const syncApps = useCallback(async (
        apps: ApplicationModel[],
        sharedTopicsApp?: ApplicationModel | null,
        _concurrencyLimit?: number,
        preserveLogs: boolean = false,
        publishAfterSync: boolean = false
    ): Promise<BulkOperationResult> => {
        const organizationId = options.organizationId;
        if (!organizationId) return { successCount: 0, errorCount: 1, errors: ['No organization was available for the sync batch.'], acceptedJobs: [], validationFailures: [] };
        setIsProcessing(true);
        if (!preserveLogs) clearLogs();
        try {
            const result = await voicifyApi.queueManagedModuleSyncBatch({
                organizationId,
                applicationIds: apps.map(app => app.id).filter((id): id is string => !!id),
                sharedTopicsApplicationId: sharedTopicsApp?.id,
                publishAfterSync
            });
            if (result.resultType !== 'Ok') {
                const errors = result.errors?.length ? result.errors : ['Unable to queue module sync batch.'];
                errors.forEach(addLog);
                return { successCount: 0, errorCount: apps.length, errors, acceptedJobs: [], validationFailures: [] };
            }
            const errors = result.data.validationFailures.map(failure => failure.error);
            addLog(`Batch ${result.data.batchId} queued with ${result.data.acceptedJobs.length} job(s). Monitor it in Queue Management.`);
            errors.forEach(addLog);
            const failedApps = new Set(result.data.validationFailures.map(failure => failure.applicationId));
            return {
                successCount: apps.filter(app => !failedApps.has(app.id)).length,
                errorCount: failedApps.size,
                errors,
                batchId: result.data.batchId,
                acceptedJobs: result.data.acceptedJobs,
                validationFailures: result.data.validationFailures
            };
        } finally {
            setIsProcessing(false);
        }
    }, [addLog, clearLogs, options.organizationId]);

    const syncAndPublishApps = useCallback((
        apps: ApplicationModel[],
        sharedTopicsApp?: ApplicationModel | null,
        concurrencyLimit?: number,
        preserveLogs: boolean = false
    ) => syncApps(apps, sharedTopicsApp, concurrencyLimit, preserveLogs, true), [syncApps]);

    const publishApps = useCallback(async (apps: ApplicationModel[], _concurrencyLimit?: number, preserveLogs: boolean = false): Promise<BulkOperationResult> => {
        const organizationId = options.organizationId;
        if (!organizationId) return { successCount: 0, errorCount: 1, errors: ['No organization was available for the publish batch.'], acceptedJobs: [], validationFailures: [] };
        setIsProcessing(true);
        if (!preserveLogs) clearLogs();
        try {
            const result = await voicifyApi.queueManagedBackupPublishBatch({ organizationId, applicationIds: apps.map(app => app.id).filter((id): id is string => !!id) });
            if (result.resultType !== 'Ok') {
                const errors = result.errors?.length ? result.errors : ['Unable to queue backup and publish batch.'];
                errors.forEach(addLog);
                return { successCount: 0, errorCount: apps.length, errors, acceptedJobs: [], validationFailures: [] };
            }
            const errors = result.data.validationFailures.map(failure => failure.error);
            addLog(`Batch ${result.data.batchId} queued with ${result.data.acceptedJobs.length} job(s). Monitor it in Queue Management.`);
            errors.forEach(addLog);
            const failedApps = new Set(result.data.validationFailures.map(failure => failure.applicationId));
            return {
                successCount: apps.filter(app => !failedApps.has(app.id)).length,
                errorCount: failedApps.size,
                errors,
                batchId: result.data.batchId,
                acceptedJobs: result.data.acceptedJobs,
                validationFailures: result.data.validationFailures
            };
        } finally {
            setIsProcessing(false);
        }
    }, [addLog, clearLogs, options.organizationId]);

    return { isProcessing, logs, syncSingleModule, syncApps, syncAndPublishApps, publishApps, appendLog: addLog, clearLogs };
};

export default useBulkAppOperations;
